import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { LookupItem } from '../../../core/services/lookup.service';
import { AuthContext } from '../../../auth/services/auth.context';
import { SimpleCodeValue } from '../../../core/models/code-value.model';
import { UserManagementService } from '../../user-management/user-management.service';
import { ManageComplaintService } from '../../../core/services/manage-complaints.service';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { provideNativeDateAdapter } from '@angular/material/core';
import { ComplaintCategoryMasterVo, ComplaintCategoryVo, ComplaintService } from '../../../core/services/complaint.service';
import { ReportService } from '../../../core/services/report.service';

@Component({
  selector: 'app-job-card-reports',
  standalone: true,
  imports: [
    MatCardModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    CommonModule,
    MatDatepickerModule
  ],
  templateUrl: './job-card-reports.component.html',
  styleUrl: './job-card-reports.component.scss',
  providers: [provideNativeDateAdapter()],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobCardReportsComponent implements OnInit, AfterViewInit {

  requestForm = new FormGroup({
    areaId: new FormControl(null),
    departmentId: new FormControl(null),
    employeeId: new FormControl(null),
    jobCardStatus: new FormControl(null),
    complaintStatus: new FormControl(null),
    startDate: new FormControl<Date | null>(null, Validators.required),
    endDate: new FormControl<Date | null>(null, Validators.required),
    categoryId: new FormControl(null),
    masterComplaintId: new FormControl(null),
  });

  jobCardStatus: LookupItem[] = [];
  areas: SimpleCodeValue[] = [];
  departments: SimpleCodeValue[] = [];
  employees: SimpleCodeValue[] = [];
  complaintStatus: LookupItem[] = [];

  categoryNameOptions: ComplaintCategoryVo[] = [];
  categoryOptions : ComplaintCategoryMasterVo[] = [];

  constructor(
    private authContext: AuthContext,
    private userManagementService: UserManagementService,
    private manageComplaintService: ManageComplaintService,
    private complaintService: ComplaintService,
    private cdr: ChangeDetectorRef,
    private reportService: ReportService
  ) {
  }

  ngOnInit(): void {
    const lookUps = this.authContext.getLookups();
    this.jobCardStatus = lookUps['JOBCARD_STATUS_TYPE'];
    this.complaintStatus = lookUps['COMPLAINT_STATUS_TYPE'];
    this.getUserZoneAreas();
    this.getDepartments();
    this.getEmployees();
    this.getComplaintCategoryNames();
    this.setDefaultDates();
  }

  ngAfterViewInit(): void {
    this.cdr.detectChanges();
  }

  onReset() {
    this.requestForm.reset({});
    this.setDefaultDates();
  }

  getUserZoneAreas() {
    this.userManagementService.getUserAreas().subscribe({
      next: (response) => {
        this.areas = response;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('POST error:', error);
      },
    });
  }

  getComplaintCategoryNames() {
    this.complaintService.getUserComplaintCategory().subscribe(categories => {
      this.categoryNameOptions = categories;
      this.cdr.detectChanges();
    });
  }

  onCategorySelected(selectedCategoryId: number) {
    const selectedCategoryDetails = this.categoryNameOptions.find(category => category.categoryId === selectedCategoryId);
    if (selectedCategoryDetails) {
      this.complaintService.getMasterComplaintCategories(selectedCategoryDetails.categoryId).subscribe(masterComplaints => {
        this.categoryOptions = masterComplaints;
        this.cdr.detectChanges();
      });
    }
  }

  getEmployees() {
    this.manageComplaintService.getEmployeesWithAssignableComplaints().subscribe({
      next: (response) => {
        this.employees = response;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('POST error:', error);
      },
    });
  }


  private getDepartments(): void {
    this.manageComplaintService.getAccessibleDepartments().subscribe({
        next: (response) => {
          this.departments = response;
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error loading departments:', error);
        }
    });
  }

  isFormFilled(): boolean {
    return this.requestForm.valid;
  }
  generateJobCardReport() {
    const formValue = this.requestForm.value;

    // Build params only with values that are present
    const params: any = {};
    if (formValue.areaId) { params.areaId = formValue.areaId; }
    if (formValue.categoryId) { params.categoryId = formValue.categoryId; }
    if (formValue.departmentId) { params.departmentId = formValue.departmentId; }
    if (formValue.employeeId) { params.employeeId = formValue.employeeId; }
    if (formValue.masterComplaintId) { params.masterComplaintId = formValue.masterComplaintId; }
    if (formValue.jobCardStatus) { params.jobCardStatus = formValue.jobCardStatus; }
    if (formValue.complaintStatus) { params.complaintStatus = formValue.complaintStatus; }
    if (formValue.startDate) { params.startDate = this.formatDate(formValue.startDate as Date); }
    if (formValue.endDate) { params.endDate = this.formatDate(formValue.endDate as Date); }

    const reportName = 'JobCardReportMesFS';

    this.reportService.downloadReport(reportName, params).subscribe({
      next: (response: any) => {
        this.downloadPDF(response, reportName);
      },
      error: (error: any) => {
        console.error('Error generating job card report:', error);
      }
    });
  }

  private downloadPDF(blob: any, reportName: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${reportName}_${new Date().getTime()}.pdf`;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private setDefaultDates(): void {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 30);
    this.requestForm.patchValue({ startDate: start, endDate: end });
    this.cdr.detectChanges();
  }

}
